import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, List, ListItem, ListItemButton, Typography } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import { useDeviceFavorites } from '@/hooks/use-device-favorites';
import { getDeviceDisplayLabel } from '@/lib/device-display';
import { colors, typography as t, radius } from '@/theme/tokens';

/** Roughly six rows before it starts scrolling, so the nav below stays reachable. */
const MAX_HEIGHT = 186;

interface FavoritesNavProps {
  collapsed: boolean;
}

/**
 * Starred machines, as direct links in the rail.
 *
 * Three rules this deliberately follows, each one a failure mode it avoids:
 *
 *   1. Renders nothing while loading — no spinner, no skeleton. This is the
 *      first nav element in the product that issues a query on every page, and
 *      a rail that hesitates would degrade every page to buy one convenience.
 *   2. Renders nothing when empty. An empty state in the navigation is
 *      permanent clutter for everyone who never stars anything.
 *   3. Renders nothing when collapsed. A column of identical star icons
 *      identifies no machine, so the icon-only variant would be decoration.
 *
 * Favorites whose device row is gone are skipped rather than linked: the star is
 * keyed to hardware and outlives the device, so a dormant entry is expected, not
 * an error. It comes back on its own if that machine re-enrolls.
 */
export function FavoritesNav({ collapsed }: FavoritesNavProps) {
  const { favorites } = useDeviceFavorites();
  const location = useLocation();

  const linkable = favorites.filter((f) => !!f.device_id);
  if (collapsed || linkable.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          mx: '12px',
          mt: 1.5,
          mb: 0.75,
          pt: 1.25,
          borderTop: '1px solid',
          borderColor: colors.sidebar.divider,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        {/* No tooltip here on purpose. This header sits directly above the
            links people are aiming for, so a hover hint covers the very rows
            they are reaching for. The beta signal lives on the Favorites chip
            in the devices toolbar, where hovering costs nothing. */}
        <Typography
          component="span"
          sx={{
            fontSize: 9.5,
            fontWeight: t.fontWeight.semibold,
            letterSpacing: '.13em',
            textTransform: 'uppercase',
            color: 'rgba(139,157,181,.72)',
          }}
        >
          Favorites
        </Typography>
        <Typography
          component="span"
          sx={{ ml: 'auto', fontSize: 9.5, color: 'rgba(139,157,181,.55)' }}
        >
          {linkable.length}
        </Typography>
      </Box>

      <List
        dense
        disablePadding
        sx={{ px: '12px', maxHeight: MAX_HEIGHT, overflowY: 'auto' }}
      >
        {linkable.map((f) => {
          const to = `/devices/${f.device_id}`;
          const isActive = location.pathname.startsWith(to);
          // Uppercased to match the devices table and the device-detail
          // breadcrumb, which both do it. The same machine reading
          // "c4a559758432" here and "C4A559758432" there looks like two
          // different devices at a glance.
          const label = getDeviceDisplayLabel({
            number: f.number,
            serial_number: f.serial_number,
            machine_id: f.machine_id,
          }).toUpperCase();
          return (
            <ListItem key={f.machine_id} disablePadding sx={{ mb: '2px' }}>
              <ListItemButton
                component={RouterLink}
                to={to}
                sx={{
                  py: '4px',
                  px: '8px',
                  gap: 1,
                  borderRadius: `${radius.md}px`,
                  color: isActive ? colors.sidebar.textActive : colors.sidebar.text,
                  bgcolor: isActive ? colors.sidebar.hoverBg : 'transparent',
                  '&:hover': { bgcolor: colors.sidebar.hoverBg },
                }}
              >
                <StarIcon sx={{ fontSize: 12, color: colors.primary.main, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Typography>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </>
  );
}
